import {tx, ptx, usePageText} from '../lib/page-text';
import { useSearchParams } from '../lib/localized-router';
import { RequestFeedback } from '../components/RequestFeedback';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useAuth } from '../lib/auth-store';
import { Modal, Code } from '../components/ui';
import { PlusIcon } from '../components/icons';

type Tab = 'workspace' | 'team' | 'billing' | 'security';

export function SettingsPage() {
  usePageText();
  const [params,setParams]=useSearchParams();
  const requested=params.get('tab') ?? (params.has('billing')?'billing':'workspace');
  const tab:Tab=['workspace','team','billing','security'].includes(requested)?requested as Tab:'workspace';
  const setTab=(value:Tab)=>{const next=new URLSearchParams(params);next.set('tab',value);next.delete('billing');setParams(next);};
  return (
    <div>
      <h1 className="h1">{tx("Settings")}</h1>
      <p className="subtitle">{tx("Workspace info, team members, billing, and security")}</p>
      <div className="tabs">
        <button className={`tab ${tab === 'workspace' ? 'active' : ''}`} onClick={() => setTab('workspace')}>{tx("Workspace")}</button>
        <button className={`tab ${tab === 'team' ? 'active' : ''}`} onClick={() => setTab('team')}>{tx("Team")}</button>
        <button className={`tab ${tab === 'billing' ? 'active' : ''}`} onClick={() => setTab('billing')}>{tx("Billing & plan")}</button>
        <button className={`tab ${tab === 'security' ? 'active' : ''}`} onClick={() => setTab('security')}>{tx("Security")}</button>
      </div>
      {tab === 'workspace' && <WorkspaceTab/>}
      {tab === 'team' && <TeamTab/>}
      {tab === 'billing' && <BillingTab/>}
      {tab === 'security' && <SecurityTab/>}
    </div>
  );
}

function BillingTab() {
  usePageText();
  const role=useAuth(state=>state.user?.role);
  const canManage=role==='OWNER'||role==='ADMIN';
  const returnTo=(outcome:string)=>{const url=new URL(window.location.href);url.searchParams.set('tab','billing');url.searchParams.set('billing',outcome);return url.toString();};
  const openCheckout=(data:any)=>{if(typeof data?.url!=='string'||new URL(data.url).protocol!=='https:')throw new Error('The billing service did not return a secure checkout link. Please retry.');window.location.assign(data.url);};
  const status = useQuery({ queryKey: ['billing-status'], queryFn: async () => (await api.get('/me/billing/status')).data });
  const usage  = useQuery({ queryKey: ['billing-usage'],  queryFn: async () => (await api.get('/me/billing/usage')).data });
  const plans  = useQuery({ queryKey: ['billing-plans'],  queryFn: async () => (await api.get('/me/billing/plans')).data });
  const access = useQuery({ queryKey: ['billing-access'], queryFn: async () => (await api.get('/me/billing/access')).data });
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const checkout = useMutation({
    mutationFn: async (plan: string) => (await api.post('/me/billing/checkout', {
      plan,
      cycle,
      successUrl: returnTo('success'),
      cancelUrl: returnTo('cancel'),
    })).data,
    onSuccess: openCheckout,
  });
  const portal = useMutation({
    mutationFn: async () => (await api.post('/me/billing/portal', { returnUrl: window.location.href })).data,
    onSuccess: openCheckout,
  });

  if(!status.data)return <RequestFeedback pending={status.isPending} failed={status.isError} retry={()=>void status.refetch()}/>;
  const s: any = status.data;
  const u: any = usage.data;
  const a: any = access.data;
  const trialing = s?.subscription?.status === 'TRIALING';

  return (
    <>
      <RequestFeedback pending={false} failed={status.isError} hasData retry={()=>void status.refetch()}/>
      {new URLSearchParams(window.location.search).get('billing')==='success' && <p role="status">{tx("Returned from checkout. Your plan updates after the payment provider confirms it.")}</p>}
      {(checkout.error || portal.error) && <p className="error" role="alert">{(checkout.error as any)?.response?.data?.message ?? (portal.error as any)?.response?.data?.message ?? checkout.error?.message ?? portal.error?.message}</p>}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3>{tx("Current plan")}</h3>
        <div className="flex gap-md" style={{ marginTop: 4 }}>
          <span className="badge info" style={{ fontSize: 13, padding: '4px 12px' }}>{s?.currentPlan}</span>
          {s?.subscription && (
            <span className="muted" style={{ fontSize: 13 }}>
              · {s.subscription.status}
              {trialing && s.subscription.trialEndsAt
                ? <>{tx(" · free trial ends ")}{new Date(s.subscription.trialEndsAt).toLocaleDateString()}</>
                : <>{tx(" · period ends ")}{new Date(s.subscription.currentPeriodEnd).toLocaleDateString()}</>}
            </span>
          )}
        </div>
        {a && !a.subscribed && s?.configured && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 8, fontSize: 13,
                        background: a.allowed ? 'rgba(24,201,100,.08)' : 'rgba(243,18,96,.08)',
                        border: `1px solid ${a.allowed ? 'rgba(24,201,100,.3)' : 'rgba(243,18,96,.3)'}` }}>
            {a.allowed
              ? a.trial.mode === 'days'
                ? <>{tx("🎁 Free trial: ")}<strong>{ptx("{v0} day{v1} left", {v0: (a.trial.remainingDays), v1: (a.trial.remainingDays === 1 ? '' : 's')})}</strong>{a.trial.endsAt ? <>{tx(" (until ")}{new Date(a.trial.endsAt).toLocaleDateString()})</> : null}{tx(" — subscribe below to keep streaming after it ends.")}</>
                : <>{tx("🎁 Free trial: ")}<strong>{ptx("{v0} of {v1} live minutes left", {v0: (a.trial.remainingMinutes), v1: (a.trial.limitMinutes)})}</strong>{tx(" — subscribe below to keep streaming after they're used.")}</>
              : <>{tx("⛔ Your free trial is over — going live is paused until you subscribe to a plan below.")}</>}
          </div>
        )}
        {!s?.configured && (
          <div style={{ marginTop: 14, padding: 12, background: 'rgba(245,165,36,.1)', border: '1px solid rgba(245,165,36,.3)', borderRadius: 8, fontSize: 13 }}>{tx("Plan upgrades are currently unavailable. Contact your workspace administrator if you need to change your plan.")}</div>
        )}
        {s?.tenantBillingCustomerId && s?.configured && (
          <div style={{ marginTop: 14 }}>
            <button className="btn outline" onClick={() => portal.mutate()} disabled={portal.isPending || !canManage}>
              {portal.isPending ? tx("Opening…") : tx("Manage billing in Stripe →")}
            </button>
            <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>{tx("Update card, download invoices, cancel.")}</p>
          </div>
        )}
      </div>

      <RequestFeedback pending={access.isPending} failed={access.isError} hasData={!!access.data} retry={()=>void access.refetch()}/>
      <RequestFeedback pending={usage.isPending} failed={usage.isError} hasData={!!usage.data} retry={()=>void usage.refetch()}/>
      {u && u.limits && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="row-between" style={{ marginBottom: 4 }}>
            <h3 style={{ margin: 0 }}>{tx("This month's usage")}</h3>
            <span className="muted" style={{ fontSize: 12 }}>
              {new Date(u.periodStart).toLocaleDateString()} – {new Date(u.periodEnd).toLocaleDateString()}
            </span>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 14 }}>{tx("Usage can take time to appear. Reaching a plan limit may pause the affected action; review your remaining allowance before a large event.")}</p>
          <div style={{ display: 'grid', gap: 10 }}>
            <UsageBar label={tx("Streamer hours")}        used={u.usage.streamerHours}        limit={u.limits.streamerHours}         unit="h"   over={u.overages?.streamerHours} />
            <UsageBar label={tx("Recording storage")}     used={u.usage.recordingGB}          limit={u.limits.recordingGB}           unit="GB"  over={u.overages?.recordingGB} />
            <UsageBar label={tx("Bandwidth (egress)")}    used={u.usage.bandwidthGB}          limit={u.limits.bandwidthGB}           unit="GB"  over={u.overages?.bandwidthGB} />
            <UsageBar label={tx("API calls")}             used={u.usage.apiCalls}             limit={u.limits.apiCallsPerMonth}      unit=""    over={u.overages?.apiCalls} />
            {u.limits.transcriptionMinutes > 0 && (
              <UsageBar label={tx("Transcription minutes")} used={u.usage.transcriptionMinutes} limit={u.limits.transcriptionMinutes} unit="min" over={u.overages?.transcription} />
            )}
            <div className="muted" style={{ fontSize: 12 }}>{tx("Peak concurrent viewers seen this month: ")}<strong>{u.usage.concurrentViewersPeak.toLocaleString()}</strong>
              {u.limits.concurrentViewers ? <>{tx(" (plan caps at ")}{u.limits.concurrentViewers.toLocaleString()})</> : null}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="row-between">
          <h3>{tx("Choose your plan")}</h3>
          {plans.data?.some((p: any) => p.yearlyPrice > 0) && (
            <div className="tabs" style={{ margin: 0 }}>
              <button className={`tab ${cycle === 'monthly' ? 'active' : ''}`} onClick={() => setCycle('monthly')}>{tx("Monthly")}</button>
              <button className={`tab ${cycle === 'yearly' ? 'active' : ''}`} onClick={() => setCycle('yearly')}>{tx("Yearly")}</button>
            </div>
          )}
        </div>
        <p className="muted" style={{ fontSize: 13, marginTop: -4, marginBottom: 16 }}>{tx("Review the checkout summary, billing period and cancellation terms before subscribing.")}</p>
        <RequestFeedback pending={plans.isPending} failed={plans.isError} hasData={!!plans.data} retry={()=>void plans.refetch()}/>
        {!canManage && <p className="muted">{tx("Ask a workspace owner or administrator to manage billing.")}</p>}
        {plans.isSuccess && !plans.data?.some((p:any)=>p.monthlyPrice>0) && <p className="muted">{tx("No paid plans are available right now.")}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 14 }}>
          {(plans.data ?? []).filter((p: any) => p.monthlyPrice > 0).map((p: any) => {
            const isCurrent = s?.currentPlan === p.code;
            const price = cycle === 'yearly' && p.yearlyPrice > 0 ? p.yearlyPrice : p.monthlyPrice;
            const per = cycle === 'yearly' && p.yearlyPrice > 0 ? '/yr' : '/mo';
            return (
              <div key={p.code} className="card" style={{ background: 'var(--bg-elevated)', borderColor: isCurrent ? 'var(--primary)' : 'var(--border)' }}>
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>{p.name}</h3>
                  {isCurrent
                    ? <span className="badge info">{tx("current")}</span>
                    : p.trialDays > 0 && <span className="badge" style={{ background: 'rgba(24,201,100,.12)', color: 'var(--success)' }}>{ptx("{v0}-day free trial", {v0: (p.trialDays)})}</span>}
                </div>
                {p.description && <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>{p.description}</p>}
                <div style={{ fontSize: 32, fontWeight: 600, marginTop: 8, letterSpacing: '-0.02em' }}>
                  ${(price / 100).toLocaleString()}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-mute)' }}>{per}</span>
                </div>
                <ul style={{ paddingLeft: 16, margin: '12px 0 16px', color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.8 }}>
                  <li>{ptx("{v0} concurrent viewers", {v0: (p.concurrentViewers.toLocaleString())})}</li>
                  <li>{ptx("{v0} streamer hours / mo", {v0: (p.streamerHours.toLocaleString())})}</li>
                  <li>{ptx("{v0} GB recording", {v0: (p.recordingGB.toLocaleString())})}</li>
                  <li>{ptx("{v0} GB bandwidth", {v0: (p.bandwidthGB.toLocaleString())})}</li>
                  {p.transcriptionMinutes > 0 && <li>{ptx("{v0} transcription min", {v0: (p.transcriptionMinutes.toLocaleString())})}</li>}
                </ul>
                <button
                  className="btn"
                  disabled={isCurrent || checkout.isPending || !s?.configured || !canManage}
                  onClick={() => checkout.mutate(p.code)}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {isCurrent ? tx("Current plan")
                    : p.trialDays > 0 && !s?.subscription ? ptx("Start {v0}-day free trial", {v0: (p.trialDays)})
                    : ptx("Subscribe to {v0}", {v0: (p.name)})}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function UsageBar({ label, used, limit, unit, over }: { label: string; used: number; limit: number; unit: string; over?: boolean }) {
  usePageText();
  const safeLimit = Math.max(1, limit);
  const pct = Math.min(100, Math.round((used / safeLimit) * 100));
  const display = (n: number) => {
    if (unit === 'GB' || unit === 'h') return Number(n).toFixed(2);
    return Math.round(n).toLocaleString();
  };
  // Three colour bands: green ≤70%, amber 70–95%, red >95% or `over`.
  const tone = over || pct > 95 ? 'var(--danger)' : pct > 70 ? '#f5a524' : 'var(--success)';
  return (
    <div>
      <div className="row-between" style={{ fontSize: 13, marginBottom: 4 }}>
        <span>{label}</span>
        <span className="muted">
          {display(used)}{unit ? ' ' + unit : ''} / {display(limit)}{unit ? ' ' + unit : ''}
          {over ? <span style={{ color: 'var(--danger)', marginLeft: 6 }}>{tx("over limit")}</span> : null}
        </span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: tone, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function WorkspaceTab() {
  usePageText();
  const canEdit=useAuth(state=>['OWNER','ADMIN'].includes(state.user?.role??''));
  const qc = useQueryClient();
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: async () => (await api.get('/me/tenant')).data });
  const [name, setName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (tenant.data) { setName(tenant.data.name); setOwnerEmail(tenant.data.ownerEmail ?? ''); }
  }, [tenant.data]);

  const update = useMutation({
    mutationFn: async () => (await api.patch('/me/tenant', { name, ownerEmail })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant'] }); setSaved(true); setTimeout(() => setSaved(false), 2000); },
  });

  if(!tenant.data)return <RequestFeedback pending={tenant.isPending} failed={tenant.isError} retry={()=>void tenant.refetch()}/>;

  return (
    <div className="card">
      <h3 style={{ marginBottom: 18 }}>{tx("Workspace")}</h3>
      <RequestFeedback pending={false} failed={tenant.isError} hasData retry={()=>void tenant.refetch()}/>
      {update.isError && <p role="alert" className="error">{(update.error as any)?.response?.data?.message ?? tx("Could not save workspace settings. Try again.")}</p>}
      <div className="grid-2">
        <div>
          <label className="label" htmlFor="workspace-name">{tx("Workspace name")}</label>
          <input id="workspace-name" className="input" value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label">{tx("Slug")}</label>
          <input className="input mono" value={tenant.data?.slug} disabled />
          <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>{tx("Slugs can't be changed (would break stream URLs).")}</p>
        </div>
        <div>
          <label className="label" htmlFor="workspace-email">{tx("Billing / contact email")}</label>
          <input className="input" id="workspace-email" type="email" disabled={!canEdit} value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">{tx("Plan")}</label>
          <input className="input" value={tenant.data?.plan} disabled />
        </div>
      </div>
      <div className="row-between" style={{ marginTop: 18 }}>
        <span className="muted" style={{ fontSize: 12 }}>{ptx("Created {v0}", {v0: (new Date(tenant.data?.createdAt).toLocaleDateString())})}</span>
        <div className="flex gap">
          {saved && <span style={{ color: 'var(--success)', fontSize: 12 }}>{tx("✓ Saved")}</span>}
          <button className="btn" disabled={update.isPending || !canEdit} onClick={() => update.mutate()}>{update.isPending ? tx("Saving…") : tx("Save changes")}</button>
        </div>
      </div>
    </div>
  );
}

function TeamTab() {
  usePageText();
  const qc=useQueryClient(),{user}=useAuth();
  const canManage=['OWNER','ADMIN'].includes(user?.role??'');
  const team=useQuery({queryKey:['team'],queryFn:async()=>(await api.get('/me/team')).data});
  const [showInvite,setShowInvite]=useState(false),[reveal,setReveal]=useState<{email:string;password:string}|null>(null);
  const refresh=()=>qc.invalidateQueries({queryKey:['team']});
  const remove=useMutation({mutationFn:async(id:string)=>(await api.delete(`/me/team/${id}`)).data,onSuccess:refresh});
  const restore=useMutation({mutationFn:async(id:string)=>(await api.post(`/me/team/${id}/restore`)).data,onSuccess:refresh});
  const updateRole=useMutation({mutationFn:async({id,role}:{id:string;role:string})=>(await api.patch(`/me/team/${id}/role`,{role})).data,onSuccess:refresh});
  const actions=[remove,restore,updateRole],busy=actions.some(a=>a.isPending),error=actions.find(a=>a.isError)?.error as any;
  return <div>
    <div className="row-between" style={{marginBottom:16}}><h3 style={{margin:0}}>{tx("Team members")}</h3>{canManage&&<button className="btn" onClick={()=>setShowInvite(true)}>{ptx("{v0} Add member", {v0: (<PlusIcon/>)})}</button>}</div>
    <p className="muted">{tx("Deactivate access when someone leaves. Their account stays available to restore. Keep at least one active owner.")}</p>
    <RequestFeedback pending={team.isPending} failed={team.isError} hasData={!!team.data} retry={()=>void team.refetch()}/>
    {error&&<p role="alert" className="error">{error?.response?.data?.message??tx("Could not update team access. Try again.")}</p>}
    <div className="card dashboard-table" style={{padding:0}}><table><thead><tr><th>{tx("Member")}</th><th>{tx("Role")}</th><th>{tx("Status")}</th><th>{tx("Last login")}</th><th>{tx("Actions")}</th></tr></thead><tbody>
      {team.data?.map((member:any)=><tr key={member.id}><td><strong>{member.name??tx("Team member")}</strong><div className="muted">{member.email}{member.id===user?.id?tx(" (you)"):''}</div></td><td>
        {user?.role==='OWNER'&&member.id!==user.id?<select className="select" aria-label={'Role for '+member.email} value={member.role} disabled={busy} onChange={e=>{actions.forEach(a=>a.reset());updateRole.mutate({id:member.id,role:e.target.value});}}>{['OWNER','ADMIN','MEMBER','VIEWER'].map(role=><option key={role}>{role}</option>)}</select>:<span className="badge">{member.role}</span>}
      </td><td><span className={'badge '+(member.active?'live':'')}>{member.active?tx("Active"):tx("Inactive")}</span></td><td className="muted">{member.lastLoginAt?new Date(member.lastLoginAt).toLocaleDateString():tx("Never")}</td><td>
        {canManage&&member.id!==user?.id&&(member.role!=='OWNER'||user?.role==='OWNER')&&<button className="btn outline sm" disabled={busy} onClick={()=>{actions.forEach(a=>a.reset());if(!member.active)restore.mutate(member.id);else if(confirm('Deactivate '+member.email+'? They will lose workspace access. You can restore the account later.'))remove.mutate(member.id);}}>{member.active?tx("Deactivate"):tx("Restore access")}</button>}
      </td></tr>)}
    </tbody></table></div>
    {showInvite&&<InviteModal onClose={()=>setShowInvite(false)} onCreated={d=>{setShowInvite(false);setReveal(d);void refresh();}}/>}
    {reveal&&<RevealPasswordModal email={reveal.email} password={reveal.password} onClose={()=>setReveal(null)}/>}
  </div>;
}

function InviteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (d: { email: string; password: string }) => void }) {
  usePageText();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'MEMBER' | 'VIEWER'>('MEMBER');
  const [err, setErr] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => (await api.post('/me/team', { email, name, role })).data,
    onSuccess: (data) => onCreated({ email: data.email, password: data.temporaryPassword }),
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not add member'),
  });
  return (
    <Modal onClose={onClose}>
      <h2>{tx("Add a team member")}</h2>
      <p className="modal-subtitle">{tx("A temporary password will be generated for you to share privately. No invitation email is sent. The member should change it under Settings → Security after signing in.")}</p>
      <label className="label" htmlFor="team-name">{tx("Name")}</label>
      <input id="team-name" className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("Alex Smith")}/>
      <div style={{ height: 12 }}/>
      <label className="label" htmlFor="team-email">{tx("Email")}</label>
      <input id="team-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={tx("alex@example.com")}/>
      <div style={{ height: 12 }}/>
      <label className="label" htmlFor="team-role">{tx("Role")}</label>
      <select id="team-role" className="select" value={role} onChange={(e) => setRole(e.target.value as any)}>
        <option value="ADMIN">{tx("ADMIN — manage team, streams, keys, webhooks")}</option>
        <option value="MEMBER">{tx("MEMBER — manage workspace content")}</option>
        <option value="VIEWER">{tx("VIEWER — read-only")}</option>
      </select>
      {err && <div className="error">{err}</div>}
      <div className="row-between" style={{ marginTop: 20 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!email || !name || create.isPending} onClick={() => create.mutate()}>{create.isPending ? tx("Adding…") : tx("Add member")}</button>
      </div>
    </Modal>
  );
}

function RevealPasswordModal({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  usePageText();
  return (
    <Modal onClose={onClose}>
      <h2>{tx("Member added")}</h2>
      <p className="modal-subtitle">{ptx("Share these credentials privately with {v0}. No email was sent. Ask them to change the password under Settings → Security after signing in.", {v0: (<strong>{email}</strong>)})}</p>
      <label className="label">{tx("Email")}</label>
      <Code value={email}/>
      <div style={{ height: 12 }}/>
      <label className="label">{tx("Temporary password")}</label>
      <Code value={password} large/>
      <div className="row-between" style={{ marginTop: 20 }}>
        <span/>
        <button className="btn" onClick={onClose}>{tx("Done")}</button>
      </div>
    </Modal>
  );
}

function SecurityTab() {
  usePageText();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: async () => (await api.post('/me/change-password', { currentPassword: current, newPassword: next })).data,
    onSuccess: () => { setDone(true); setCurrent(''); setNext(''); setTimeout(() => setDone(false), 3000); },
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not change'),
  });
  return (
    <div className="card">
      <h3 style={{ marginBottom: 18 }}>{tx("Change password")}</h3>
      <label className="label" htmlFor="current-password">{tx("Current password")}</label>
      <input className="input" id="current-password" type="password" autoComplete="current-password" value={current} onChange={(e) => { setErr(null); setCurrent(e.target.value); }}/>
      <div style={{ height: 12 }}/>
      <label className="label" htmlFor="new-password">{tx("New password (at least 8 chars)")}</label>
      <input className="input" id="new-password" type="password" autoComplete="new-password" value={next} onChange={(e) => { setErr(null); setNext(e.target.value); }}/>
      {err && <div className="error">{err}</div>}
      <div className="row-between" style={{ marginTop: 18 }}>
        {done && <span style={{ color: 'var(--success)', fontSize: 12 }}>{tx("✓ Password changed")}</span>}
        <span/>
        <button className="btn" disabled={!current || next.length < 8 || change.isPending} onClick={() => change.mutate()}>{change.isPending ? tx("Saving…") : tx("Update password")}</button>
      </div>
    </div>
  );
}
